// The order workflow's shared pieces (shared domain/orderStage.ts): an order's stage as a pill, Sales' side or
// Assembly's; what changed in a revision; and Assembly opening a confirmed order for Sales to change (a reason,
// what to change and a time limit), used by Assembly Orders and Sales Orders alike.

import { useState } from 'react';
import { ORDER_STAGES, awaitsAcceptance, orderStage, type OrderSummary } from '@technohull/shared';
import { useUnlockOrder } from '../features/orders.js';
import { InlineError, Modal, Pill } from './ui.js';

type StageOrder = Pick<OrderSummary, 'status' | 'revisionNumber' | 'acceptedRevision' | 'inProduction'>;

/** the order's stage in the words of the side looking at it; an order in production with a new revision to
 *  accept says so */
export function StagePill({ order, side }: { order: StageOrder; side: 'sales' | 'assembly' }) {
  const stage = orderStage(order);
  const s = ORDER_STAGES[stage];
  const again = stage === 'in_production' && awaitsAcceptance(order);
  return (
    <Pill kind={again ? 'warn' : s.tone || undefined} title={again ? 'In production, with a new revision to accept' : s.hint}>
      {s[side]}{again ? (side === 'assembly' ? ' · to accept' : ' · changed') : ''}
    </Pill>
  );
}

export interface Change { changeType: string; entity: string; oldValue?: string | null; newValue?: string | null }
export function parseChanges(json: string | null | undefined): Change[] {
  if (!json) return [];
  try { return JSON.parse(json) as Change[]; } catch { return []; }
}
const changeClass = (t: string) => (t === 'added' || t === 'custom_added' ? 'rev-add' : t === 'removed' || t === 'custom_removed' ? 'rev-rem' : 'rev-mod');
const changeText = (c: Change) =>
  c.changeType === 'added' || c.changeType === 'custom_added' ? `+ ${c.entity}${c.changeType === 'custom_added' ? ' (request)' : ''}`
  : c.changeType === 'removed' || c.changeType === 'custom_removed' ? `− ${c.entity}${c.changeType === 'custom_removed' ? ' (request)' : ''}`
  : `${c.entity}: ${c.oldValue || '-'} → ${c.newValue || '-'}`;

/** a revision's changes: additions, removals and changed values, each marked */
export function ChangeList({ changes }: { changes: Change[] }) {
  if (!changes.length) return null;
  return (
    <ul className="rev-changes">
      {changes.map((c, i) => <li key={i} className={changeClass(c.changeType)}>{changeText(c)}</li>)}
    </ul>
  );
}

const LIMITS = [
  { hours: 4, label: '4 hours' },
  { hours: 24, label: '1 day' },
  { hours: 72, label: '3 days' },
  { hours: 168, label: '1 week' },
  { hours: 0, label: 'Until they save' },
];

/** Assembly opens a confirmed order for Sales: its creator can then save one new revision, and it locks again */
export function OpenForSalesModal({ order, onClose }: { order: Pick<OrderSummary, 'id' | 'orderNumber' | 'vesselName' | 'customer' | 'createdBy'>; onClose: () => void }) {
  const unlock = useUnlockOrder();
  const [reason, setReason] = useState('');
  const [instructions, setInstructions] = useState('');
  const [hours, setHours] = useState(24);
  const go = () =>
    unlock.mutate(
      { id: order.id, reason: reason.trim(), instructions: instructions.trim() || undefined, expiresInHours: hours || undefined },
      { onSuccess: onClose },
    );
  return (
    <Modal
      title={`Open ${order.orderNumber} for Sales`}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!reason.trim() || unlock.isPending} onClick={go}>
            {unlock.isPending ? 'Opening…' : 'Open for Sales'}
          </button>
        </>
      }
    >
      <p className="ow-lead">
        {[order.vesselName, order.customer].filter(Boolean).join(' · ') || order.orderNumber}. Its creator can change it and save it
        once, as a new revision; it then locks again and comes back here to accept. They are told by email.
      </p>
      <label className="ow-field">
        <span className="field-label">Why it is being opened</span>
        <input className="field" autoFocus value={reason} maxLength={1000} onChange={(e) => setReason(e.target.value)} placeholder="The customer wants a different engine" />
      </label>
      <label className="ow-field">
        <span className="field-label">What to change <span className="muted">(optional)</span></span>
        <textarea className="field" rows={2} value={instructions} maxLength={2000} onChange={(e) => setInstructions(e.target.value)} placeholder="Change the engines only" />
      </label>
      <div className="ow-field">
        <span className="field-label" id="ow-limit">Open for</span>
        <div className="ow-limits" role="radiogroup" aria-labelledby="ow-limit">
          {LIMITS.map((l) => (
            <button key={l.hours} type="button" role="radio" aria-checked={hours === l.hours} className={`chip${hours === l.hours ? ' active' : ''}`} onClick={() => setHours(l.hours)}>
              {l.label}
            </button>
          ))}
        </div>
      </div>
      {unlock.isError && <InlineError error={unlock.error} onDismiss={() => unlock.reset()} />}
    </Modal>
  );
}
